function MenuIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="12" cy="19" r="2" />
        </svg>
    );
}

function InstallIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 4v12m0 0l-4-4m4 4l4-4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" />
        </svg>
    );
}

function CheckIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

interface Step {
    number: number;
    title: string;
    description: string;
    icon: React.ReactNode;
}

interface AndroidInstructionsProps {
    onInstall?: () => void;
    hasNativePrompt?: boolean;
}

export function AndroidInstructions({ onInstall, hasNativePrompt }: AndroidInstructionsProps) {
    if (hasNativePrompt && onInstall) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-700 mb-4">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.6 11.48V8.65l1.48-2.56a.5.5 0 00-.86-.5l-1.54 2.67a8.56 8.56 0 00-3.17-.6c-1.1 0-2.15.2-3.16.6L8.77 5.6a.5.5 0 10-.86.5l1.48 2.56v2.82c-2.36.78-4.15 2.5-4.15 4.72v.64h16.52v-.64c0-2.22-1.79-3.94-4.16-4.72zm-9.1 3.39a.86.86 0 110-1.72.86.86 0 010 1.72zm7 0a.86.86 0 110-1.72.86.86 0 010 1.72z" />
                    </svg>
                    <span className="font-medium">Instalación en Android</span>
                </div>

                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                        <CheckIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                        <span className="text-gray-800 font-medium text-sm">Listo para instalar</span>
                        <p className="text-gray-500 text-xs mt-0.5">
                            Tocá el botón de abajo para agregar Abasto Virtual a tu pantalla de inicio.
                        </p>
                    </div>
                </div>

                <button
                    onClick={onInstall}
                    className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                    <InstallIcon className="w-5 h-5" />
                    Instalar App
                </button>
            </div>
        );
    }

    const steps: Step[] = [
        {
            number: 1,
            title: 'Tocá el menú',
            description: 'Los tres puntos en la esquina superior derecha de Chrome',
            icon: <MenuIcon className="w-6 h-6 text-green-500" />,
        },
        {
            number: 2,
            title: 'Seleccioná "Instalar app" o "Añadir a pantalla de inicio"',
            description: 'La opción puede variar según la versión de Chrome',
            icon: <InstallIcon className="w-6 h-6 text-green-500" />,
        },
        {
            number: 3,
            title: 'Tocá "Instalar" para confirmar',
            description: 'La app se agregará a tu pantalla de inicio',
            icon: <CheckIcon className="w-6 h-6 text-green-500" />,
        },
    ];

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-700 mb-4">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.6 11.48V8.65l1.48-2.56a.5.5 0 00-.86-.5l-1.54 2.67a8.56 8.56 0 00-3.17-.6c-1.1 0-2.15.2-3.16.6L8.77 5.6a.5.5 0 10-.86.5l1.48 2.56v2.82c-2.36.78-4.15 2.5-4.15 4.72v.64h16.52v-.64c0-2.22-1.79-3.94-4.16-4.72zm-9.1 3.39a.86.86 0 110-1.72.86.86 0 010 1.72zm7 0a.86.86 0 110-1.72.86.86 0 010 1.72z" />
                </svg>
                <span className="font-medium">Pasos para instalar en Android</span>
            </div>

            <div className="space-y-3">
                {steps.map((step) => (
                    <div key={step.number} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {step.number}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-800 font-medium text-sm">{step.title}</span>
                                {step.icon}
                            </div>
                            <p className="text-gray-500 text-xs mt-0.5">{step.description}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-700">
                    <strong>Tip:</strong> Para la mejor experiencia, usá Chrome u otro navegador basado en Chromium (Edge, Samsung Internet, etc.).
                </p>
            </div>
        </div>
    );
}
